/* eslint-disable import/prefer-default-export */

import { simpleFetch as fetcher } from '@api/fetchers';
import { emptyObject } from '@constants/empty';
import { useDSTMetaInfo } from '@stores/dst/dstStore';
import { getDefaultHost, getTestSeriesHost } from '@utils/api-helper/host';
import { redirectToRoute } from '@utils/route';
import { DST_ATTEMPT_MAP, DST_MAX_QUIZ_ATTEMPTED } from './constants';

const TestSeriesHost = getTestSeriesHost();
const DefaultHost = getDefaultHost();

export const fetchDSTAttemptsStatus = async ({ goalUID, accessToken }) => {
  return fetcher(
    `${DefaultHost}v1/uplus/daily_scholarship_test_details/?goal_uid=${goalUID}`,
    { authorization: accessToken }
  ).then(({ data, extraBlockInfo }) => {
    return {
      ...data[0],
      ctaList: extraBlockInfo.ctaList
    };
  });
};

export const fetchDSTQuizAPIsInParallel = async ({
  goalUID,
  accessToken,
  searchToken
}) => {
  const promises = [];
  promises.push(
    fetcher(`${TestSeriesHost}scholarship/v1/${goalUID}/quiz_details`, {
      authorization: accessToken,
      searchToken
    })
  );

  promises.push(
    fetcher(`${TestSeriesHost}scholarship/v1/${goalUID}/class_preference`, {
      authorization: accessToken,
      searchToken
    })
  );

  promises.push(
    fetchDSTAttemptsStatus({
      goalUID,
      accessToken
    })
  );

  const res = await Promise.all(promises);
  return res;
};

export const handleDSTRedirectAndFetch = async ({
  isPlusUser,
  goalUID,
  goalSlug,
  accessToken,
  searchToken,
  isServer,
  res,
  onClient,
  setLoading
}) => {
  const { setState } = useDSTMetaInfo;

  if (setLoading) {
    setLoading(true);
  }

  try {
    if (isPlusUser) {
      return redirectToRoute(isServer, res, '/');
    }

    const responses = await fetchDSTQuizAPIsInParallel({
      goalUID,
      accessToken,
      searchToken
    });

    const [quizInfo, classPreference, attemptsInfo] = responses;

    if (attemptsInfo?.state === DST_ATTEMPT_MAP.RESUME_NOW) {
      return redirectToRoute(
        isServer,
        res,
        `/goal/${goalSlug}/${goalUID}/scholarship-test/${attemptsInfo.quizUid}`
      );
    }

    if (
      attemptsInfo?.state === DST_ATTEMPT_MAP.VIEW_SUMMARY ||
      attemptsInfo?.state === DST_ATTEMPT_MAP.WON ||
      attemptsInfo?.attemptsLeft === DST_MAX_QUIZ_ATTEMPTED
    ) {
      return redirectToRoute(
        isServer,
        res,
        `/goal/${goalSlug}/${goalUID}/scholarship-test/${attemptsInfo.quizUid}/attempts/${attemptsInfo.sessionUid}`
      );
    }

    if (setLoading) {
      setLoading(false);
    }

    if (onClient) {
      return setState((state) => ({
        ...state,
        quizInfo,
        classPreference,
        attemptsInfo
      }));
    }

    return {
      quizInfo,
      classPreference,
      attemptsInfo
    };
  } catch (error) {
    redirectToRoute(isServer, res, '/');
    return emptyObject;
  }
};

export const getIsDSTFlow = (asPath) => {
  return /\/scholarship-test/.test(asPath);
};

export const handleDSTTakingRedirection = async ({
  goalSlug,
  goalUID,
  accessToken,
  isServer,
  res
}) => {
  try {
    const attemptsInfo = await fetchDSTAttemptsStatus({
      goalUID,
      accessToken
    });

    if (attemptsInfo?.state !== DST_ATTEMPT_MAP.RESUME_NOW) {
      if (attemptsInfo?.attemptsLeft) {
        return redirectToRoute(
          isServer,
          res,
          `/goal/${goalSlug}/${goalUID}/scholarship-test`
        );
      }

      return redirectToRoute(
        isServer,
        res,
        `/goal/${goalSlug}/${goalUID}/scholarship-test/${attemptsInfo.quizUid}/attempts/${attemptsInfo.sessionUid}`
      );
    }
    return attemptsInfo;
  } catch (error) {
    return redirectToRoute(isServer, res, '/');
  }
};

export const generateDSTDeepLinkForMobile = async () => {
  const defaultLink = `https://unacademy.com${window.location.pathname}`;
  // scholarship_test_intro/:goalId/:classPref/true/:quizId

  return new Promise((resolve) => {
    if (window.branch && window.branch.init) {
      // In case of browsers such as Brave, certain tracking scripts and cookies are blocked, hence window.branch and window.branch.init will be undefined. In that case, just open the default URL.
      window.branch.init(process.env.BRANCH_IO_LIVE_KEY, {}, (err) => {
        if (err) {
          return resolve(defaultLink);
        }

        const data = {
          campaign: 'mweb',
          channel: 'mweb',
          feature: 'mweb',
          stage: 'new user',
          tags: [],
          alias: '',
          data: {
            deep_link_for_story_object: defaultLink,
            $desktop_url: defaultLink,
            $og_title: document.title
          }
        };

        const callback = (deepLinkGenerationError, link) => {
          if (deepLinkGenerationError) {
            return resolve(defaultLink);
          }

          return resolve(link);
        };

        const generatedDeepLink = window.branch.link(data, callback);

        return generatedDeepLink || defaultLink;
      });
    } else return resolve(defaultLink);
  });
};
